import { useMemo, useState } from "react";
import { Download, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { getMockData, META } from "../lib/mockData";
import { downloadCSV, formatPct } from "../lib/csv";

const REPORT_TYPES = [
  { value: "department", label: "By department" },
  { value: "floor", label: "By floor" },
  { value: "workplace", label: "By workplace" },
  { value: "meeting-room", label: "By meeting room" },
  { value: "daily", label: "By date range (daily)" },
];

const RANGES = [
  { value: "60", label: "Last 60 days" },
  { value: "30", label: "Last 30 days" },
  { value: "14", label: "Last 14 days" },
  { value: "7", label: "Last 7 days" },
];

export default function Reports() {
  const data = useMemo(() => getMockData(), []);
  const [type, setType] = useState("department");
  const [range, setRange] = useState("60");

  const handleDownload = (overrideType, overrideRange) => {
    const reportType = overrideType || type;
    const reportRange = overrideRange || range;
    const days = Number(reportRange);
    const sliced = data.history.slice(-days);

    if (reportType === "department") {
      const rows = data.departmentStats.map((d) => ({
        department: d.department,
        allocated_desks: d.allocatedDesks,
        used_desks: d.usedDesks,
        unused_desks: d.unusedDesks,
        occupancy_rate_pct: (d.occupancyRate * 100).toFixed(1),
      }));
      downloadCSV(`hq-signal_department_${reportRange}d.csv`, rows, [
        { key: "department", label: "Department" },
        { key: "allocated_desks", label: "Allocated desks" },
        { key: "used_desks", label: "Used desks" },
        { key: "unused_desks", label: "Unused desks" },
        { key: "occupancy_rate_pct", label: "Occupancy rate (%)" },
      ]);
    } else if (reportType === "floor") {
      const rows = data.floorStats.map((f) => ({
        floor: f.floor,
        total_workplaces: f.totalWorkplaces,
        used_workplaces: f.usedWorkplaces,
        unused_workplaces: f.unusedWorkplaces,
        occupancy_rate_pct: (f.occupancyRate * 100).toFixed(1),
      }));
      downloadCSV(`hq-signal_floor_${reportRange}d.csv`, rows);
    } else if (reportType === "workplace") {
      const rows = data.workplaceUtilization.map((w) => ({
        workplace_id: w.id,
        floor: w.floor,
        zone: w.zone,
        department: w.department,
        active_days: w.activeDays,
        occupancy_rate_pct: (w.occupancyRate * 100).toFixed(1),
      }));
      downloadCSV(`hq-signal_workplace_${reportRange}d.csv`, rows);
    } else if (reportType === "meeting-room") {
      const rows = data.meetingRoomStats.map((r) => ({
        room: `Room ${r.name}`,
        floor: r.floor,
        capacity: r.capacity,
        avg_usage: r.avgUsage,
        efficiency_pct: (r.efficiency * 100).toFixed(1),
        booking_rate_pct: (r.bookingRate * 100).toFixed(1),
        no_show_rate_pct: (r.noShowRate * 100).toFixed(1),
      }));
      downloadCSV(`hq-signal_meeting-rooms_${reportRange}d.csv`, rows);
    } else if (reportType === "daily") {
      const rows = sliced.map((d) => ({
        date: d.date,
        weekday: d.weekday,
        occupancy_rate_pct: (d.occupancyRate * 100).toFixed(1),
      }));
      downloadCSV(`hq-signal_daily_${reportRange}d.csv`, rows);
    }

    toast.success("CSV downloaded", {
      description: `${REPORT_TYPES.find((r) => r.value === reportType).label} · ${reportRange} days`,
    });
  };

  return (
    <div data-testid="page-reports">
      <PageHeader
        title="Reports & exports"
        subtitle="Download CSV exports tailored to your audience."
        testId="reports-header"
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="border-slate-200 shadow-sm lg:col-span-2" data-testid="card-export-form">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading text-lg font-medium text-slate-900">
              Build your export
            </CardTitle>
            <p className="text-xs text-slate-500">
              Choose the granularity and time window. CSV is generated locally —
              no data leaves your browser.
            </p>
          </CardHeader>
          <CardContent className="space-y-6 p-6">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <Label htmlFor="rep-type" className="text-xs font-medium text-slate-500">
                  Report type
                </Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger id="rep-type" data-testid="report-type" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REPORT_TYPES.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="rep-range" className="text-xs font-medium text-slate-500">
                  Date range
                </Label>
                <Select value={range} onValueChange={setRange}>
                  <SelectTrigger id="rep-range" data-testid="report-range" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RANGES.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600" data-testid="export-preview">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="h-4 w-4 text-blue-600" />
                <span className="font-medium text-slate-900">
                  Preview · {REPORT_TYPES.find((r) => r.value === type).label}
                </span>
              </div>
              <ul className="mt-2 space-y-1 text-xs text-slate-600">
                <li>· Window: {range} days</li>
                <li>· Rows: {previewRowCount(type, data, range)}</li>
                <li>· Avg desk occupancy: {formatPct(data.kpis.deskOccupancyRate)}</li>
                <li>· Format: UTF-8 CSV with header row</li>
              </ul>
            </div>

            <Button
              onClick={() => handleDownload()}
              size="lg"
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="download-csv-btn"
            >
              <Download className="mr-2 h-4 w-4" />
              Download CSV
            </Button>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm" data-testid="card-quick-exports">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading text-lg font-medium text-slate-900">
              Quick exports
            </CardTitle>
            <p className="text-xs text-slate-500">
              One-click downloads for common views.
            </p>
          </CardHeader>
          <CardContent className="space-y-2 p-4">
            {REPORT_TYPES.map((r) => (
              <Button
                key={r.value}
                variant="outline"
                className="w-full justify-between border-slate-200 text-slate-700 hover:bg-slate-50"
                onClick={() => {
                  setType(r.value);
                  handleDownload(r.value, range);
                }}
                data-testid={`quick-export-${r.value}`}
              >
                {r.label}
                <Download className="h-4 w-4" />
              </Button>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function previewRowCount(type, data, range) {
  if (type === "department") return data.departmentStats.length;
  if (type === "floor") return data.floorStats.length;
  if (type === "workplace") return data.workplaceUtilization.length;
  if (type === "meeting-room") return data.meetingRoomStats.length;
  if (type === "daily") return Number(range);
  return 0;
}
